import { useState } from 'react';
import { uploadChargeImages } from '../api/chargeImages';

interface UseUploadChargeImagesResult {
  uploadImages: (files: File[]) => Promise<void>;
  isUploading: boolean;
  error: string | null;
}

/**
 * Hook to handle charge image uploads
 * - Manages upload state
 * - Handles errors
 * - Calls success callback when upload completes
 */
export function useUploadChargeImages(
  jobId: number,
  chargeId: number,
  onSuccess?: () => void
): UseUploadChargeImagesResult {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadImages = async (files: File[]) => {
    if (files.length === 0) return;

    setIsUploading(true);
    setError(null);

    try {
      await uploadChargeImages(jobId, chargeId, files);

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const errorMessage = 'Failed to upload images';
      setError(errorMessage);
      console.error('Error uploading charge images:', err);
      throw new Error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadImages,
    isUploading,
    error,
  };
}
